import React, { useEffect, useState } from 'react';

import { BaseControlProps } from '../../models/BaseControlProps';
import { DataTable, Pager, Span } from '@tag/tag-components-react-v4';
import { MessageAPIService } from '../../services/MessageAPIService';
import { MessageGetRequest } from '../../models/MessageGetRequest';
import { Message } from '../../models/Message';
import { useAuth } from '../../services/AuthContext';
import { UtilService } from '../../services/UtilService';
import { useMessage } from '../../components/Common/MessageContext/MessageContext';
import { Box, Button, Card, CardContent, Divider, IconButton, Skeleton, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import { AttachFile, Description, PictureAsPdf } from '@mui/icons-material';
import ImageIcon from '@mui/icons-material/Image';
import MessageDetail from '../../components/MessageDetail/MessageDetail';
import { MessageAttachment } from '../../models/MessageAttachment';

const Messages = (props: BaseControlProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const pageSize = 15;
    const [scopeGet, applyGet] = useState<MessageGetRequest>({
        pageSize: pageSize,
        pageNo: 0,
        filterSearchString: "",
        orderByString: "",
    });
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [loading, isLoading] = useState<boolean>(true);
    const [messages, setMessages] = useState<Message[]>([]);
    const authContext = useAuth();
    const { showMessage } = useMessage();
    const messageApiService = new MessageAPIService();
    const [showDetail, setShowDetail] = React.useState(false);
    const [messageDetail, setMessageDetail ] = React.useState<Message>();


    useEffect(() => {
        const getAllMessagesAsync = async () => {
            await getAllMessages();
        }
        getAllMessagesAsync();
    }, []);

    const getAllMessages = () => {
        isLoading(true);
        messageApiService.getAllMessages(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                if(props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                setMessages(response.response?.messages ?? []);
                const pageCount =
                    response.response && response.response?.messages.length > 0
                        ? Math.ceil(response.response.totalCount / pageSize)
                        : 0;
                setPageCount(pageCount && pageCount > 0 ? pageCount : 0);
            }
            isLoading(false);
        })
    }

    function getAttachmentIcon(fileName: string) {
        const extension = fileName.split('.').pop()?.toLowerCase();
          
        switch (extension) {
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
                return <ImageIcon sx={{ color: 'teal !important', fontSize: '20px' }} />;
            case 'pdf':
                return <PictureAsPdf sx={{ color: 'red !important', fontSize: '20px' }} />;
            case 'doc':
            case 'docx':
                return <Description sx={{ color: 'blue', fontSize: '20px' }} />;
            default:
                return <AttachFile sx={{ fontSize: '20px' }} />;
        }
    }

    function renderViewButton(message: Message) {
        if (isMobile) {
            return (
                <Button
                    variant="outlined"
                    onClick={() => {
                        setMessageDetail(message);
                        setShowDetail(true);
                    }}
                    endIcon={<ReadMoreIcon />}
                >
                    View
                </Button>
            )
        } else {
            return (
            <IconButton
                aria-label='read-message'
                size='small'
                color='primary'
                onClick={() => {
                    setMessageDetail(message);
                    setShowDetail(true);
                }}
            >
                <ReadMoreIcon />
            </IconButton>
            );
        }
    }

    const columns = React.useMemo(
        () => [
        { 
            accessorKey: 'createdOn', 
            header: 'Date', 
            meta: { dataType: 'date' },
            cell: ({ row }: any) => {
                const date = row.original.createdOn;
                return date == '1900-01-01T00:00:00'
                    ? '-'
                    : moment(date).format('DD-MM-YY HH:mm');
            }, 
        },
        { 
            accessorKey: 'messageContent', 
            header: 'Message',
            cell: ({ row }: any) => {
                return getFormatedMessage(row.original.messageContent);
            } 
        },
        {
            header: 'Attachments',
            cell: ({row}: any) => {
                return getMessageAttachments(row.original.attachments);
            }
        },
        {
            accessorKey: 'view',
            header: () => <div style={{ textAlign: 'center' }}>View</div>,
            cell: ({row}: any) => {
                return renderViewButton(row.original);
            },
            meta: {
                getCellProps: () => {
                return { style: { textAlign: 'center' } };
                },
            },
        },
        ],
        [],
    );
    
    const handlePageChange = async (e: any) => {
        setPage(e.page - 1);
        scopeGet.pageNo = e.page - 1;
        applyGet(scopeGet);

        await getAllMessages();
    };

    const renderheaderView = () => {
        return (
            <Box sx={{ width: '100%', pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography component="h2" variant="h5" gutterBottom>
                        Messages
                    </Typography>
                </Box>
                <Divider />
            </Box>
        );
    }

    const EmptyState = () => (
        <Box 
            sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.background.paper,
            }}
        >
            <Typography variant="body1" color="text.secondary">
            No payslips found
            </Typography>
        </Box>
    );

    // Loading skeleton for mobile
    const MobileLoadingSkeleton = () => (
        <Box sx={{ mb: 2 }}>
        {[...Array(3)].map((_, index) => (
            <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
                <Box display="flex" justifyContent="flex-end">
                <Skeleton variant="rectangular" width={100} height={36} />
                </Box>
            </CardContent>
            </Card>
        ))}
        </Box>
    );

    const getFormatedMessage = (message: string) => {
        const content = message
            .replace(/&nbsp;/g, ' ') // Replace &nbsp; with regular space first
            .replace(/<\/?[^>]+(>|$)/g, "") // Strip HTML tags
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim() // Remove leading/trailing whitespace
            .slice(0, 60); // Limit to 60 characters

        return content + (message.length > 60 ? '...' : '');
    }

    const getMessageAttachments = (attachments: MessageAttachment[]) => {
        return (
            <div style={{ display: 'flex', gap: '8px' }}>
                {attachments.map((attachment: any) => (
                    <div key={attachment.portalMessageAttachmentId}>
                        {getAttachmentIcon(attachment.attachmentName)}
                    </div>
                ))}
            </div>
        );
    }

    if (isMobile) {
        return (
            <>
                {renderheaderView()}

                <div>
                {loading ? (
                <MobileLoadingSkeleton />
                    ) : messages.length === 0 ? (
                    <EmptyState />
                    ) : (
                    messages.map((message) => (
                        <Card key={message.createdOn} sx={{ mb: 1 }}>
                            <CardContent>
                                <Typography variant="subtitle2">
                                Date:
                                {message.createdOn === '1900-01-01T00:00:00' 
                                    ? '-' 
                                    : moment(message.createdOn).format('DD-MM-YY HH:mm')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    
                                {getFormatedMessage(message.messageContent)}
                                </Typography>
                                {message.attachments.length > 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                    Attachments: {getMessageAttachments(message.attachments)}
                                    </Typography>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                {renderViewButton(message)}
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
                {!loading && messages.length > 0 && (
                    <Pager
                        accent='blue'
                        page={page + 1}
                        pageCount={pageCount}
                        onPageRequest={(e) => handlePageChange(e)}
                        style={{ justifyContent: 'center', padding: '5px' }}
                    />
                )}
            </div>

                {messageDetail && (
                    <MessageDetail 
                        visible={showDetail}
                        setVisible={setShowDetail}
                        message={messageDetail}
                        markAsRead={false}
                    />
                )}
            </>
        )
    }

    return(
        <>
            {renderheaderView()}

            <div style={{ background: '#fff', }}>
                <div style={{ height: 'auto'  }}>
                    <DataTable
                        isLoading={loading} 
                        loadingElement={<Span accent="blue">Loading ...</Span>} 
                        accent="blue" 
                        columns={columns} 
                        data={messages}
                        tableProps={{
                            role: 'presentation'
                        }} 
                    />
                </div>
                <Pager
                    accent='blue'
                    page={page + 1}
                    pageCount={pageCount}
                    onPageRequest={(e) => handlePageChange(e)}
                    style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                />
            </div>
            {messageDetail && (
                <MessageDetail 
                    visible={showDetail}
                    setVisible={setShowDetail}
                    message={messageDetail}
                    markAsRead={false}
                />
            )}
        </>
    )
}
export default Messages;